export default function ChallengesSection() {
  return (
    <section className="py-20 bg-white">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Common <span className="text-teal-600">Challenges We Solve</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Hoarder houses present unique obstacles that traditional buyers and real estate agents can't or won't handle. We specialize in solving these problems.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 mb-16">
          <div className="bg-gray-50 rounded-xl p-8">
            <div className="w-14 h-14 bg-red-100 rounded-lg flex items-center justify-center mb-6">
              <i className="ri-home-2-line text-2xl text-red-600"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-4">Non-Livable Conditions</h3>
            <p className="text-gray-700 mb-4 leading-relaxed">
              Many hoarder houses have deteriorated to the point where they're deemed non-livable by traditional standards. Blocked exits, compromised structural integrity, or severe sanitation issues make the property unsuitable for immediate occupancy.
            </p>
            <p className="text-gray-700 leading-relaxed">
              <strong className="text-gray-900">Our solution:</strong> We buy properties in any condition and have the resources to restore them to livable standards after purchase. The current condition doesn't affect our ability to make you a fair offer.
            </p>
          </div>

          <div className="bg-gray-50 rounded-xl p-8">
            <div className="w-14 h-14 bg-orange-100 rounded-lg flex items-center justify-center mb-6">
              <i className="ri-time-line text-2xl text-orange-600"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-4">Overwhelming Cleanup Task</h3>
            <p className="text-gray-700 mb-4 leading-relaxed">
              The sheer volume of items in a hoarder house can be paralyzing. Sorting through decades of accumulated belongings, deciding what to keep or discard, and physically removing everything requires enormous time, energy, and emotional strength.
            </p>
            <p className="text-gray-700 leading-relaxed">
              <strong className="text-gray-900">Our solution:</strong> You don't touch a single item. Our experienced team handles all sorting, removal, and disposal. We've cleared houses with 40+ years of accumulation - nothing is too much for us.
            </p>
          </div>

          <div className="bg-gray-50 rounded-xl p-8">
            <div className="w-14 h-14 bg-yellow-100 rounded-lg flex items-center justify-center mb-6">
              <i className="ri-money-dollar-circle-line text-2xl text-yellow-600"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-4">High Cleanup Costs</h3>
            <p className="text-gray-700 mb-4 leading-relaxed">
              Professional junk removal, dumpster rentals, cleaning services, and potential repairs can easily cost $10,000 to $50,000 or more. Many property owners simply don't have these funds available, especially if they've inherited the property.
            </p>
            <p className="text-gray-700 leading-relaxed">
              <strong className="text-gray-900">Our solution:</strong> We absorb all cleanup costs. You pay nothing for removal, cleaning, or repairs. These costs are factored into our offer, but you never write a check or hire a contractor.
            </p>
          </div>

          <div className="bg-gray-50 rounded-xl p-8">
            <div className="w-14 h-14 bg-green-100 rounded-lg flex items-center justify-center mb-6">
              <i className="ri-shield-cross-line text-2xl text-green-600"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-4">Health and Safety Hazards</h3>
            <p className="text-gray-700 mb-4 leading-relaxed">
              Hoarder houses often contain health hazards like mold, pest infestations, biohazards, or structural dangers. These conditions require specialized handling and protective equipment that most people don't have access to.
            </p>
            <p className="text-gray-700 leading-relaxed">
              <strong className="text-gray-900">Our solution:</strong> Our team is trained in proper safety protocols and works with licensed professionals for hazardous material removal. We handle everything safely and legally, protecting both our workers and the environment.
            </p>
          </div>

          <div className="bg-gray-50 rounded-xl p-8">
            <div className="w-14 h-14 bg-blue-100 rounded-lg flex items-center justify-center mb-6">
              <i className="ri-emotion-sad-line text-2xl text-blue-600"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-4">Emotional Burden</h3>
            <p className="text-gray-700 mb-4 leading-relaxed">
              Dealing with a hoarder house is emotionally exhausting, especially when it involves a deceased or incapacitated loved one. The guilt, grief, and overwhelm can make it nearly impossible to take action.
            </p>
            <p className="text-gray-700 leading-relaxed">
              <strong className="text-gray-900">Our solution:</strong> We approach every situation with compassion and understanding. You don't have to face this alone. We handle the physical work while respecting the emotional significance of the situation.
            </p>
          </div>

          <div className="bg-gray-50 rounded-xl p-8">
            <div className="w-14 h-14 bg-purple-100 rounded-lg flex items-center justify-center mb-6">
              <i className="ri-user-forbid-line text-2xl text-purple-600"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-4">Buyer Rejection</h3>
            <p className="text-gray-700 mb-4 leading-relaxed">
              Traditional buyers are immediately turned off by hoarder houses. Even investors often pass because of the extensive cleanup required. Real estate agents may refuse to list the property or insist on cleanup before listing.
            </p>
            <p className="text-gray-700 leading-relaxed">
              <strong className="text-gray-900">Our solution:</strong> We specialize in hoarder houses and see the potential beyond the clutter. We make fair offers based on the property's true value, not its current appearance. No cleanup required before we buy.
            </p>
          </div>
        </div>

        <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-2xl p-10">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <img 
                src="https://public.readdy.ai/ai/img_res/580ddfdc16fdfe80b8edbe4ba13a09c6.jpg"
                alt="Before and after property transformation"
                className="w-full h-80 object-cover rounded-xl shadow-lg"
              />
            </div>
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">We See Potential, Not Problems</h3>
              <p className="text-gray-700 mb-4 leading-relaxed">
                While others see an impossible situation, we see an opportunity to help someone in need while acquiring a property with great potential. We have the experience, resources, and systems in place to handle even the most challenging hoarder houses.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Our team has successfully purchased and restored hundreds of hoarder properties. We know exactly what needs to be done, how long it will take, and what it will cost. This expertise allows us to make fair offers quickly and close fast.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
